/**
 * Tracking Service
 *
 * Generates a short unique tracking key, reads stored ad tracking data
 * from localStorage, sends it to the saveTrackingData API, and provides
 * a helper to open WhatsApp with the tracking key embedded in the message.
 */

import { getTrackingData, getMetaVisitCount } from "./adTracking";

const API_URL =
    "https://us-central1-myweddinglyinvites-backend.cloudfunctions.net/saveTrackingData";

/**
 * Read a browser cookie by name.
 */
function getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Generate a short unique tracking key.
 * Format: base36-timestamp + "-" + 3 random alphanumeric chars
 * e.g. "1kx4z2f-a3x"
 */
export function generateTrackingKey(): string {
    const ts = Date.now().toString(36); // compact timestamp
    const rand = Math.random().toString(36).substring(2, 5); // 3 random chars
    return `${ts}-${rand}`;
}

/**
 * Send tracking data to the saveTrackingData API.
 * Returns the generated tracking key on success, or null on failure.
 */
export async function sendTrackingData(
    cardId?: string,
    userId?: string
): Promise<string | null> {
    try {
        const trackingData = getTrackingData();
        const metaVisitCount = getMetaVisitCount();
        const key = generateTrackingKey();

        const payload: Record<string, any> = {
            key,
            url: trackingData.landing_url || window.location.href,
            meta_visit_count: metaVisitCount,
        };

        // Add all tracking params
        if (trackingData.fbclid) payload.fbclid = trackingData.fbclid;
        if (trackingData.gclid) payload.gclid = trackingData.gclid;

        // fbp: prefer localStorage, fallback to _fbp cookie (set by Facebook Pixel)
        const fbp = trackingData.fbp || getCookie("_fbp");
        if (fbp) payload.fbp = fbp;
        if (trackingData.utm_source) payload.utm_source = trackingData.utm_source;
        if (trackingData.utm_medium) payload.utm_medium = trackingData.utm_medium;
        if (trackingData.utm_campaign)
            payload.utm_campaign = trackingData.utm_campaign;
        if (trackingData.utm_content)
            payload.utm_content = trackingData.utm_content;
        if (trackingData.utm_term) payload.utm_term = trackingData.utm_term;

        // Add card and user info if available
        if (cardId) payload.card_id = cardId;
        if (userId) payload.user_id = userId;

        // Fire-and-forget — don't block WhatsApp opening
        fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }).catch((err) => console.error("[TrackingService] API error:", err));

        console.log("[TrackingService] Sent tracking data:", payload);
        return key;
    } catch (e) {
        console.error("[TrackingService] Error:", e);
        return null;
    }
}

/**
 * Get userId from browser cookies.
 * Checks for loginUserId first (logged-in user), then userId (temp user).
 */
function getUserIdFromCookieSync(): string | null {
    if (typeof document === "undefined") return null;
    try {
        const cookies = document.cookie.split(";");
        // First check for logged-in user
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "loginUserId" && value) return value;
        }
        // Fallback to temp user
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "userId" && value) return value;
        }
    } catch {
        // ignore
    }
    return null;
}

const WHATSAPP_PHONE = "918708328579";

interface WhatsAppParams {
    cardId?: string;
    userId?: string;
    customMessage?: string;
}

/**
 * Open WhatsApp with tracking data sent to API.
 * The WhatsApp message will include:
 *   - Tracking key (short unique ID)
 *   - Card ID (if available)
 *   - User ID (if available)
 *   - Custom message
 */
export async function openWhatsAppWithTracking(
    params: WhatsAppParams = {}
): Promise<void> {
    const { cardId, customMessage } = params;

    // Get userId from cookie if not provided
    const userId = params.userId || getUserIdFromCookieSync() || undefined;

    // Send tracking data to API and get the key
    const key = await sendTrackingData(cardId, userId);

    // Build WhatsApp message
    const parts: string[] = [];

    if (key) parts.push(`Ref: ${key}`);
    if (cardId) parts.push(`Card: ${cardId}`);
    if (userId) parts.push(`User: ${userId}`);

    const headerLine = parts.length > 0 ? parts.join(" | ") : "";

    const message = customMessage
        ? `${headerLine}\n\n${customMessage}`
        : `${headerLine}\n\nHi! I'm interested in ordering a wedding invitation. Could you please guide me through the process?`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
        message.trim()
    )}`;

    window.open(whatsappUrl, "_blank");
}
